import React, { useContext, useState, useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next'; // Added for i18n support
import { WalletContext } from '../../contexts/WalletContext';
import { AuthContext } from '../../contexts/AuthContext';
import { getPasswordFeedback } from '../../utils/cryptoUtils';
import { validateMnemonic, isPrivateKey } from '../../utils/mnemonicUtils';
// Import secure memory utilities
import { 
  createSecureReference, 
  getSecureData, 
  secureClearData, 
  maskSensitiveText 
} from '../../utils/secureMemoryUtils';
import myWalletLogo from '../../assets/wallet-logo.png';
import { InstallPWABanner } from '../../components/InstallPWA';
import './WelcomePage.css';

// Icons (replace with actual icon components if available)
const BackIcon = () => <span className="welcome-back-icon">←</span>;
const ShieldIcon = () => <span className="welcome-security-icon">🔒</span>;
const WalletIcon = () => <span className="welcome-security-icon">💼</span>;
const KeyIcon = () => <span className="welcome-security-icon">🔑</span>;
const WarningIcon = () => <span className="welcome-warning-icon">⚠️</span>;
const ArrowRightIcon = () => <span className="welcome-arrow-icon">→</span>;
const CheckIcon = () => <span className="welcome-check-icon">✓</span>;
const InfoIcon = () => <span className="welcome-info-icon">ℹ️</span>;
const BriefcaseIcon = () => <span className="welcome-security-icon">💼</span>;
const InterfaceIcon = () => <span className="welcome-security-icon">🖥️</span>;
const LoadingIcon = () => (
  <div className="welcome-loading-indicator">
    <div className="welcome-loading-circle"></div>
    <div className="welcome-loading-circle"></div>
    <div className="welcome-loading-circle"></div>
  </div>
);

const WelcomePage = () => {
  const { t } = useTranslation();
  const location = useLocation();
  
  const {
    createWallet,
    importWallet,
    userWallets,
    setUserWallets,
    getSeedPhrase,
    setCurrentWallet,
  } = useContext(WalletContext);
  
  // Add AuthContext
  const {
    createPassword,
    createAutoPassword,
    deferPassword,
    setSessionActive
  } = useContext(AuthContext);

  // State for UI flow
  const [flowStep, setFlowStep] = useState('welcome-intro');
  const [isLoading, setIsLoading] = useState(false);
  const [showCopied, setShowCopied] = useState(false);
  const [showFullSeed, setShowFullSeed] = useState(false);

  // Form data
  const [importInput, setImportInput] = useState(''); // Can be seed phrase or private key
  const [confirmSeedPhrase, setConfirmSeedPhrase] = useState('');
  const [tempWallet, setTempWallet] = useState(null);
  
  // SECURITY IMPROVEMENT: Replace direct seed phrase storage with secure reference
  const [seedPhraseToken, setSeedPhraseToken] = useState(null);
  const [seedPhraseMasked, setSeedPhraseMasked] = useState('');
  const [seedPhraseTimeout, setSeedPhraseTimeout] = useState(null);
  
  // NEW: Add persistent storage for seed phrase to survive tab changes
  const [persistentSeedPhrase, setPersistentSeedPhrase] = useState('');
  const welcomeFlowKey = 'bitdog_welcome_flow_state';
  
  const [userConfirmed, setUserConfirmed] = useState(false);
  const [securityAcknowledged, setSecurityAcknowledged] = useState(false);
  const [selectedSeedWords, setSelectedSeedWords] = useState([]);
  const [shuffledSeedWords, setShuffledSeedWords] = useState([]);
  const [isSeedVerified, setIsSeedVerified] = useState(false);

  // Password form data
  const [walletPassword, setWalletPasswordInput] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [passwordFeedback, setPasswordFeedback] = useState(null);
  const [passwordStrength, setPasswordStrength] = useState(0);
  const [skipPassword, setSkipPassword] = useState(false);

  // Error handling
  const [error, setError] = useState(null);
  const [seedError, setSeedError] = useState(null);
  const [importError, setImportError] = useState(null);

  const [walletSetupComplete, setWalletSetupComplete] = useState(false);
  const [walletSetupData, setWalletSetupData] = useState(null);

  // NEW: Helper function that tries multiple ways to get the seed phrase
  const getSeedPhraseForUI = () => {
    // Try secure token first
    if (seedPhraseToken) {
      try {
        const phrase = getSecureData(seedPhraseToken, true);
        if (phrase) return phrase;
      } catch (e) {
        console.log("Error accessing secure seed phrase token:", e);
      }
    }
    
    // Fall back to persistent copy if token is not available
    if (persistentSeedPhrase) return persistentSeedPhrase;
    
    // Last resort - try session storage
    try {
      const savedState = sessionStorage.getItem(welcomeFlowKey);
      if (savedState) {
        const { seedPhrase } = JSON.parse(savedState);
        if (seedPhrase) return seedPhrase;
      }
    } catch (e) {
      console.error("Failed to restore welcome flow state:", e);
    }
    
    return null;
  };

  // SECURITY IMPROVEMENT: Function to securely store seed phrase
  const secureStoreSeedPhrase = (seedPhrase, timeoutMs = 3 * 60 * 1000) => {
    // Clear any existing token first
    if (seedPhraseToken) {
      secureClearData(seedPhraseToken);
    }
    
    if (!seedPhrase) {
      setSeedPhraseToken(null);
      setSeedPhraseMasked('');
      return;
    }
    
    // Create a new secure reference
    const token = createSecureReference(seedPhrase, timeoutMs);
    setSeedPhraseToken(token);
    
    // Create a masked version for display
    setSeedPhraseMasked(maskSensitiveText(seedPhrase));
    
    // NEW: Also store it persistently to survive tab changes
    setPersistentSeedPhrase(seedPhrase);
    
    // NEW: Also store in sessionStorage as backup
    try {
      sessionStorage.setItem(welcomeFlowKey, JSON.stringify({
        seedPhrase,
        step: flowStep,
        timestamp: Date.now()
      }));
    } catch (e) {
      console.error("Failed to store welcome flow state:", e);
    }
  };

  // SECURITY IMPROVEMENT: Function to clear all sensitive data
  const clearSensitiveData = () => {
    if (seedPhraseToken) {
      secureClearData(seedPhraseToken);
      setSeedPhraseToken(null);
    }
    
    // Clear confirmation input
    if (confirmSeedPhrase) {
      // Overwrite in memory before clearing
      setConfirmSeedPhrase('0'.repeat(confirmSeedPhrase.length));
      setConfirmSeedPhrase('');
    }
    
    // Clear import input
    if (importInput) {
      // Overwrite in memory before clearing
      setImportInput('0'.repeat(importInput.length));
      setImportInput('');
    }
    
    // Clear timeout to avoid memory leaks
    if (seedPhraseTimeout) {
      clearTimeout(seedPhraseTimeout);
      setSeedPhraseTimeout(null);
    }
    
    // NEW: Clear persistent seed phrase from state and storage
    if (persistentSeedPhrase) {
      setPersistentSeedPhrase('');
    }
    sessionStorage.removeItem(welcomeFlowKey);
    
    // Reset visibility
    setShowFullSeed(false);
  };

  // NEW: Effect to handle seed phrase persistence during welcome flow
  useEffect(() => {
    // Seed-related steps where we need to maintain the seed phrase
    const seedRelatedSteps = [
      'wallet-created', 
      'seed-phrase-info', 
      'verify-seed', 
      'verify-seed-manual',
      'security-confirmation'
    ];
    
    // Check if we're in a step that needs seed phrase persistence
    const needsPersistence = seedRelatedSteps.includes(flowStep);
    
    if (needsPersistence) {
      // APPROACH 1: If we have a token, make sure to get the seed phrase and store it persistently
      if (seedPhraseToken) {
        try {
          const seedPhrase = getSecureData(seedPhraseToken, true);
          if (seedPhrase && seedPhrase !== persistentSeedPhrase) {
            console.log("Storing seed phrase persistently for welcome flow");
            setPersistentSeedPhrase(seedPhrase);
            
            // Also store in sessionStorage as backup
            try {
              sessionStorage.setItem(welcomeFlowKey, JSON.stringify({
                seedPhrase,
                step: flowStep,
                timestamp: Date.now()
              }));
            } catch (e) {
              console.error("Failed to store welcome flow state:", e);
            }
          }
        } catch (e) {
          console.log("Error accessing seed phrase token:", e);
        }
      } 
      // APPROACH 2: If we don't have a token but have a persistent seed phrase, restore it
      else if (persistentSeedPhrase && persistentSeedPhrase.length > 0) {
        console.log("Restoring seed phrase from persistent state");
        secureStoreSeedPhrase(persistentSeedPhrase);
      }
      // APPROACH 3: Last resort - try to get from sessionStorage
      else {
        try {
          const savedState = sessionStorage.getItem(welcomeFlowKey);
          if (savedState) {
            const { seedPhrase } = JSON.parse(savedState);
            if (seedPhrase && seedPhrase.length > 0) {
              console.log("Restoring seed phrase from session storage");
              setPersistentSeedPhrase(seedPhrase);
              secureStoreSeedPhrase(seedPhrase);
            }
          }
        } catch (e) {
          console.error("Failed to restore welcome flow state:", e);
        }
      }
      
      // For verification step, also restore shuffled words if needed
      if ((flowStep === 'verify-seed' || flowStep === 'verify-seed-manual') && 
          shuffledSeedWords.length === 0) {
        const seedPhrase = getSeedPhraseForUI();
        if (seedPhrase) {
          console.log("Restoring shuffled words for verification");
          const seedWords = seedPhrase.split(' ');
          setShuffledSeedWords([...seedWords].sort(() => Math.random() - 0.5));
        }
      }
    }
  }, [flowStep, seedPhraseToken, persistentSeedPhrase, shuffledSeedWords.length]);

  // NEW: Handle visibility changes to restore seed phrase
  useEffect(() => {
    const seedRelatedSteps = [
      'wallet-created', 
      'seed-phrase-info', 
      'verify-seed', 
      'verify-seed-manual',
      'security-confirmation'
    ];
    
    const needsPersistence = seedRelatedSteps.includes(flowStep);
    
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible' && needsPersistence) {
        console.log("Tab became visible, checking seed phrase state");
        
        // If we don't have a valid token but have persistent data, restore it
        const hasSeedPhraseInToken = seedPhraseToken && getSecureData(seedPhraseToken, false);
        
        if (!hasSeedPhraseInToken) {
          // Try to restore from persistent state first
          if (persistentSeedPhrase) {
            console.log("Restoring seed phrase after visibility change from persistent state");
            secureStoreSeedPhrase(persistentSeedPhrase);
          } 
          // Then try session storage
          else {
            try {
              const savedState = sessionStorage.getItem(welcomeFlowKey);
              if (savedState) {
                const { seedPhrase } = JSON.parse(savedState);
                if (seedPhrase) {
                  console.log("Restoring seed phrase after visibility change from session storage");
                  setPersistentSeedPhrase(seedPhrase);
                  secureStoreSeedPhrase(seedPhrase);
                }
              }
            } catch (e) {
              console.error("Failed to restore welcome flow state:", e);
            }
          }
          
          // Special handling for verification step
          if ((flowStep === 'verify-seed' || flowStep === 'verify-seed-manual') && shuffledSeedWords.length === 0) {
            const seedPhrase = getSeedPhraseForUI();
            if (seedPhrase) {
              const seedWords = seedPhrase.split(' ');
              setShuffledSeedWords([...seedWords].sort(() => Math.random() - 0.5));
            }
          }
        }
      }
    };
    
    window.addEventListener('visibilitychange', handleVisibilityChange);
    return () => {
      window.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [flowStep, seedPhraseToken, persistentSeedPhrase, shuffledSeedWords.length]);

  // Check for deep link parameters in session storage
  useEffect(() => {
    const deepLinkChain = sessionStorage.getItem('deep_link_chain');
    const deepLinkSwapToken = sessionStorage.getItem('deep_link_swap_token');
    
    if (deepLinkChain || deepLinkSwapToken) {
      console.log('Welcome page: Detected deep link parameters', {
        chain: deepLinkChain,
        hasSwapToken: !!deepLinkSwapToken
      });
    }
  }, []);

  // Update error when step changes
  useEffect(() => {
    setError(null);
    setSeedError(null);
    setImportError(null);
    
    // Auto-focus seed input when needed
    if (flowStep === 'verify-seed' && seedInputRef.current) {
      setTimeout(() => {
        seedInputRef.current.focus();
      }, 300);
    }
  }, [flowStep]);
  
  // Check password strength when password changes
  useEffect(() => {
    if (walletPassword) {
      const feedback = getPasswordFeedback(walletPassword, t);
      setPasswordFeedback(feedback);
      setPasswordStrength(feedback.score);
    } else {
      setPasswordFeedback(null);
      setPasswordStrength(0);
    }
  }, [walletPassword, t]);

  // Add useEffect for deep link detection - place this with other useEffect hooks
  useEffect(() => {
    // Check for deep link parameters in location state
    if (location.state) {
      const { pendingChainKey, pendingTokenAddress } = location.state;
      
      if (pendingChainKey) {
        console.log(`Welcome page: Detected pending chain selection: ${pendingChainKey}`);
        sessionStorage.setItem('deep_link_chain', pendingChainKey);
      }
      
      if (pendingTokenAddress) {
        console.log(`Welcome page: Detected pending token: ${pendingTokenAddress}`);
        sessionStorage.setItem('deep_link_swap_token', pendingTokenAddress);
      }
      
      // Clear the state to prevent reapplying on future navigations
      window.history.replaceState(null, document.title, window.location.pathname);
    }
  }, [location.state]);

  // SECURITY IMPROVEMENT: Auto-hide seed phrase after a timeout
  useEffect(() => {
    if (showFullSeed) {
      // Set a timeout to automatically hide the seed phrase after 2 minutes
      const timer = setTimeout(() => {
        setShowFullSeed(false);
      }, 2 * 60 * 1000); // 2 minutes
      
      setSeedPhraseTimeout(timer);
      
      return () => {
        clearTimeout(timer);
        setSeedPhraseTimeout(null);
      };
    }
  }, [showFullSeed]);

  // SECURITY IMPROVEMENT: Clean up sensitive data when component unmounts
  useEffect(() => {
    return () => {
      clearSensitiveData();
    };
  }, []);

  // NEW: Cleanup when wallet setup is complete
  useEffect(() => {
    if (walletSetupComplete) {
      // Clear any persistent seed phrase storage when setup is complete
      setPersistentSeedPhrase('');
      sessionStorage.removeItem(welcomeFlowKey);
    }
  }, [walletSetupComplete]);

  const seedInputRef = useRef(null);

  /**
   * Handle wallet creation
   */
  const handleCreateWallet = async (e) => {
    if (e) e.preventDefault();
    setIsLoading(true);
    setError(null);

    try {
      // Create a new wallet - this now returns an object with both wallet and seedPhrase
      const result = await createWallet();
      
      // Validate wallet was created successfully
      if (!result || !result.wallet || !result.wallet.address) {
        throw new Error(t('welcome.errors.failedToCreateWallet'));
      }
      
      // Make sure we have a seed phrase
      if (!result.seedPhrase) {
        throw new Error(t('welcome.errors.failedToRetrieveSeedPhrase'));
      }
      
      // Set the temporary wallet
      setTempWallet(result.wallet);
      
      // UPDATED: Store seed phrase using both methods for redundancy
      setPersistentSeedPhrase(result.seedPhrase);
      secureStoreSeedPhrase(result.seedPhrase);
      
      // Also store in sessionStorage as backup
      try {
        sessionStorage.setItem(welcomeFlowKey, JSON.stringify({
          seedPhrase: result.seedPhrase,
          step: 'wallet-created',
          timestamp: Date.now()
        }));
      } catch (e) {
        console.error("Failed to store welcome flow state:", e);
      }
      
      // Prepare for seed verification
      const seedWords = result.seedPhrase.split(' ');
      setShuffledSeedWords([...seedWords].sort(() => Math.random() - 0.5));
      setSelectedSeedWords([]);
      
      setFlowStep('wallet-created');
    } catch (err) {
      console.error("Error creating wallet:", err);
      setError(`${t('welcome.errors.failedToCreate')}: ${err.message || t('welcome.errors.unknown')}`);
      setFlowStep('create-error');
    } finally {
      setIsLoading(false);
    }
  };

  /**
   * Handle wallet import
   */
  const handleImportWallet = async (e) => {
    if (e) e.preventDefault();
    
    if (!importInput) {
      setImportError(t('welcome.import.enterSeedPhraseOrPrivateKey'));
      return;
    }

    setIsLoading(true);
    setImportError(null);

    try {
      // Import wallet using the provided seed phrase or private key
      const result = await importWallet(importInput);
      
      // Validate the imported wallet
      if (!result || !result.wallet || !result.wallet.address) {
        throw new Error(t('welcome.errors.invalidDataReturned'));
      }
      
      // Set the temp wallet
      setTempWallet(result.wallet);
      
      // UPDATED: Securely store the seed phrase if available using both methods
      if (result.seedPhrase) {
        setPersistentSeedPhrase(result.seedPhrase);
        secureStoreSeedPhrase(result.seedPhrase);
        
        // Also store in sessionStorage as backup
        try {
          sessionStorage.setItem(welcomeFlowKey, JSON.stringify({
            seedPhrase: result.seedPhrase,
            step: 'address-info',
            timestamp: Date.now()
          }));
        } catch (e) {
          console.error("Failed to store welcome flow state:", e);
        }
        
        console.log("Imported seed phrase successfully");
      } else {
        console.log("Imported private key (no seed phrase)");
      }
      
      // Skip to address info
      setFlowStep('address-info');
    } catch (err) {
      console.error("Import error:", err);
      setImportError(`${t('welcome.errors.failedToImport')}: ${err.message || t('errors.invalidFormat')}`);
    } finally {
      setIsLoading(false);
    }
  };

  /**
   * Verify the seed phrase by selecting words in order
   */
  const handleSelectSeedWord = (word) => {
    // Add the word to selected words
    setSelectedSeedWords(prev => [...prev, word]);
    
    // Remove the word from shuffled words
    setShuffledSeedWords(prev => prev.filter(w => w !== word));
    
    // UPDATED: Use our helper function to get the seed phrase
    const seedPhrase = getSeedPhraseForUI();
    
    if (!seedPhrase) {
      setSeedError(t('welcome.errors.seedPhraseNotAvailable'));
      return;
    }
    
    // Check if all words have been selected
    if (selectedSeedWords.length + 1 === seedPhrase.split(' ').length) {
      // Check if selected words match the seed phrase
      const originalSeedWords = seedPhrase.split(' ');
      const isCorrect = [...selectedSeedWords, word].every((w, i) => w === originalSeedWords[i]);
      
      if (isCorrect) {
        setIsSeedVerified(true);
        setSeedError(null);
        setTimeout(() => {
          setFlowStep('security-confirmation');
        }, 1000);
      } else {
        setSeedError(t('welcome.seedPhrase.incorrectOrder'));
        // Reset selection
        setShuffledSeedWords([...originalSeedWords].sort(() => Math.random() - 0.5));
        setSelectedSeedWords([]);
        setIsSeedVerified(false);
      }
    }
  };

  const handleDeselectSeedWord = (word, index) => {
    // Remove the word from selectedSeedWords
    const newSelectedWords = selectedSeedWords.filter((_, i) => i !== index);
    setSelectedSeedWords(newSelectedWords);
    
    // Add the word back to shuffledSeedWords
    setShuffledSeedWords([...shuffledSeedWords, word]);
    
    // Clear any error message if it exists
    if (seedError) {
      setSeedError(null);
    }
  };

  /**
   * Manually verify that user has saved their seed phrase
   * Used as backup method if clicking verification doesn't work
   */
  const verifySeedPhraseSaved = (e) => {
    if (e) e.preventDefault();
    setSeedError(null);
    
    if (!confirmSeedPhrase) {
      setSeedError(t('welcome.seedPhrase.enterToConfirm'));
      return;
    }
    
    // UPDATED: Use our helper function to get the seed phrase
    const seedPhrase = getSeedPhraseForUI();
    
    if (!seedPhrase) {
      setSeedError(t('welcome.errors.seedPhraseNotAvailable'));
      return;
    }
    
    if (confirmSeedPhrase !== seedPhrase) {
      setSeedError(t('welcome.seedPhrase.doesNotMatch'));
      return;
    }
    
    // Verification successful
    setIsSeedVerified(true);
    
    // Move to the next step
    setFlowStep('security-confirmation');
    
    // Clear confirmation seed phrase immediately
    setConfirmSeedPhrase('');
  };

  /**
   * Set up security and finalize wallet
   */
  const handleSetupSecurity = async (e) => {
    if (e && e.preventDefault) e.preventDefault();
    if (!tempWallet) return;
    
    try {
      setIsLoading(true);
      setError(null);
      
      // Validate password requirements BEFORE changing screens
      if (!skipPassword) {
        // Check password match first
        if (walletPassword !== confirmPassword) {
          setError(t('welcome.passwords.dontMatch'));
          setIsLoading(false);
          return;
        }
        
        // Check password strength
        if (passwordStrength < 3) {
          setError(t('welcome.passwords.tooWeak'));
          setIsLoading(false);
          return;
        }
      }
      
      // Only after validation passes, show the success screen
      setFlowStep('setup-success');
      
      // NEW: Clear the temporary seed phrase immediately
      sessionStorage.removeItem(welcomeFlowKey);
      setPersistentSeedPhrase('');
      
      // Create an auto-generated password for wallet encryption (basic security)
      await new Promise(resolve => setTimeout(resolve, 1000));
      console.log("Creating auto password for wallet encryption...");
      const { success: autoSuccess, password: autoPassword } = await createAutoPassword();
      
      if (!autoSuccess) {
        throw new Error(t('welcome.errors.failedToSecureWallet'));
      }
      
      console.log("Auto password created successfully!");
      
      // Handle user password options
      await new Promise(resolve => setTimeout(resolve, 300));
      
      if (skipPassword) {
        console.log("User is skipping personal password, using auto password only");
        await deferPassword(true);
      } else {
        // Create user password - we already validated it above
        const success = await createPassword(walletPassword);
        if (!success) {
          throw new Error(t('welcome.errors.failedToCreatePassword'));
        }
        
        await deferPassword(false);
      }
      
      // Enable session and prepare wallet data
      await new Promise(resolve => setTimeout(resolve, 300));
      await setSessionActive(true);
      
      // Check if the wallet has a name, if not add one
      if (!tempWallet.name) {
        tempWallet.name = t('welcome.wallet.account', { number: userWallets.length + 1 });
      }
      
      // Create a new wallet list with the new wallet
      const newWalletsList = [...userWallets, tempWallet];
      
      // Update wallet data
      await new Promise(resolve => setTimeout(resolve, 300));
      setUserWallets(newWalletsList);
      
      // Set current wallet
      await new Promise(resolve => setTimeout(resolve, 300));
      setCurrentWallet(tempWallet);
      
      // Check if we have deep link parameters that should be applied after wallet setup
      const deepLinkChain = sessionStorage.getItem('deep_link_chain');
      const deepLinkSwapToken = sessionStorage.getItem('deep_link_swap_token');
      
      // Log the deep link parameters if they exist
      if (deepLinkChain || deepLinkSwapToken) {
        console.log('Welcome page: Applying deep link parameters after wallet setup', {
          chain: deepLinkChain,
          hasSwapToken: !!deepLinkSwapToken
        });
      }
      
      // Signal completion with data
      setWalletSetupData(tempWallet);
      setWalletSetupComplete(true);
      
      // SECURITY IMPROVEMENT: Clear sensitive data after wallet setup
      clearSensitiveData();
      
    } catch (err) {
      console.error("Wallet setup error:", err);
      setError(t('welcome.errors.setupFailed', { message: err.message || t('welcome.errors.unknown') }));
      setFlowStep('password-setup'); // Go back to password setup on error
      setIsLoading(false);
    }
  };

  /**
   * Copy text to clipboard with visual feedback
   */
  const handleCopy = (e, text) => {
    if (e) e.preventDefault();
    navigator.clipboard.writeText(text);
    setShowCopied(true);
    setTimeout(() => setShowCopied(false), 2000);
    
    // SECURITY IMPROVEMENT: Add warning about clipboard security
    setTimeout(() => {
      alert(t('settings.security.recovery.clipboardWarning', { defaultValue: 'Warning: Your clipboard can be accessed by other applications. Clear your clipboard after pasting your sensitive information.' }));
    }, 500);
  };

  /**
   * Toggle seed phrase visibility
   */
  const toggleSeedVisibility = (e) => {
    if (e) e.preventDefault();
    setShowFullSeed(!showFullSeed);
    
    // SECURITY IMPROVEMENT: Start auto-hide timer when showing the seed phrase
    if (!showFullSeed) {
      // Set up a timeout to hide it automatically after 2 minutes
      const timer = setTimeout(() => {
        setShowFullSeed(false);
      }, 2 * 60 * 1000); // 2 minutes
      
      setSeedPhraseTimeout(timer);
    } else {
      // Clear the timer if hiding
      if (seedPhraseTimeout) {
        clearTimeout(seedPhraseTimeout);
        setSeedPhraseTimeout(null);
      }
    }
  };

  /**
   * Navigate back based on current step
   */
  const handleBack = () => {
    // Define the back navigation flow
    const backMap = {
      'about-features': 'welcome-intro',
      'about-features-more': 'about-features',
      'create-intro': 'welcome-intro',
      'create-process': 'create-intro',
      'create-confirm': 'create-process',
      'import-intro': 'welcome-intro',
      'import-warning': 'import-intro',
      'wallet-created': 'create-confirm',
      'seed-phrase-info': 'wallet-created',
      'verify-seed': 'seed-phrase-info',
      'verify-seed-manual': 'seed-phrase-info',
      'security-confirmation': 'verify-seed',
      'password-setup': 'security-confirmation',
      'address-info': 'import-warning',
      'create-error': 'create-process',
      'import-error': 'import-warning',
    };
    
    const prevStep = backMap[flowStep] || 'welcome-intro';
    setFlowStep(prevStep);
  };

  /**
   * Format an address for display
   */
  const formatAddress = (address) => {
    if (!address) return '';

    // If address is long enough, show first 12 and last 8 characters
    if (address.length > 20) {
      return `${address.substring(0, 12)}...${address.substring(address.length - 8)}`;
    }

    return address;
  };

  // Calculate progress percentage based on current step
  const getProgressPercentage = () => {
    const createFlow = [
      'welcome-intro', 'about-features', 'about-features-more', 'create-intro', 'create-process', 'create-confirm',
      'wallet-created', 'seed-phrase-info', 'verify-seed', 'security-confirmation',
      'password-setup', 'setup-success'
    ];
    
    const importFlow = [
      'welcome-intro', 'about-features', 'about-features-more', 'import-intro', 'import-warning', 
      'address-info', 'security-confirmation', 'password-setup', 'setup-success'
    ];
    
    let flow = importInput ? importFlow : createFlow;
    
    // Handle error states
    if (flowStep === 'create-error') flow = createFlow;
    if (flowStep === 'import-error') flow = importFlow;
    
    const index = flow.indexOf(flowStep);
    if (index === -1) return 0;
    
    return Math.round((index / (flow.length - 1)) * 100);
  };

  // Get appropriate title for current step
  const getStepTitle = () => {
    const titles = {
      'welcome-intro': t('welcome.steps.welcomeIntro'),
      'about-features': t('welcome.steps.aboutFeatures'),
      'about-features-more': t('welcome.steps.aboutFeatures'),
      'create-intro': t('welcome.steps.createIntro'),
      'create-process': t('welcome.steps.createProcess'),
      'create-confirm': t('welcome.steps.createConfirm'),
      'import-intro': t('welcome.steps.importIntro'),
      'import-warning': t('welcome.steps.importWarning'),
      'wallet-created': t('welcome.steps.walletCreated'),
      'address-info': t('welcome.steps.addressInfo'),
      'seed-phrase-info': t('welcome.steps.seedPhraseInfo'),
      'verify-seed': t('welcome.steps.verifySeed'),
      'verify-seed-manual': t('welcome.steps.verifySeed'),
      'security-confirmation': t('welcome.steps.securityConfirmation'),
      'password-setup': t('welcome.steps.passwordSetup'),
      'setup-success': t('welcome.steps.setupSuccess'),
      'create-error': t('welcome.steps.createError'),
      'import-error': t('welcome.steps.importError'),
    };
    
    return titles[flowStep] || t('welcome.steps.walletSetup');
  };

  // Render a single seed word with selection capability
  const renderSeedWord = (word, index, isSelected = false) => {
    const handleClick = () => {
      if (!isSelected) {
        handleSelectSeedWord(word);
      }
    };
    
    return (
      <div 
        key={`${word}-${index}`} 
        className={`seed-word ${isSelected ? 'selected' : ''}`}
        onClick={handleClick}
      >
        {isSelected && <span className="seed-word-number">{selectedSeedWords.length + 1}</span>}
        {word}
      </div>
    );
  };

  return (
    <div className="welcome-container">
      {/* Brand section - now a fixed header */}
      <div className="welcome-brand-container">
        <img src={myWalletLogo} alt={t('app.name')} className="welcome-logo" />
        <div className="welcome-title-container">
          <h1 className="welcome-title">{t('app.name')}</h1>
          <p className="welcome-subtitle">{t('app.subtitle')}</p>
        </div>
      </div>
  
      {/* Step progress bar */}
      <div className="welcome-progress-container">
        <div 
          className="welcome-progress-bar" 
          style={{ width: `${getProgressPercentage()}%` }}
        ></div>
      </div>

      {/* Main content card */}
      <div className="welcome-card">
        {/* Dynamic title */}
        <h2 className="welcome-step-title">{getStepTitle()}</h2>
        
        {/* STEP: Initial Welcome */}
        {flowStep === 'welcome-intro' && (
          <div className="welcome-step-content">
            <div className="welcome-intro-content">
              <p className="welcome-tagline">
                {t('welcome.intro.tagline')}
              </p>
              <p className="welcome-description">
                {t('welcome.intro.description')}
              </p>
              
              <div className="welcome-feature-box">
                <div className="welcome-feature-row">
                  <CheckIcon /> 
                  <span>{t('welcome.intro.features.nonCustodial')}</span>
                </div>
                <div className="welcome-feature-row">
                  <CheckIcon /> 
                  <span>{t('welcome.intro.features.multiChain')}</span>
                </div>
                <div className="welcome-feature-row">
                  <CheckIcon /> 
                  <span>{t('welcome.intro.features.secure')}</span>
                </div>
              </div>
            </div>

            <div className="welcome-actions">
              <button
                type="button"
                className="welcome-primary-btn"
                onClick={() => setFlowStep('create-intro')}
                aria-label={t('welcome.buttons.createNewWallet')}
              >
                {t('welcome.buttons.createNewWallet')}
              </button>

              <button
                type="button"
                className="welcome-secondary-btn"
                onClick={() => setFlowStep('import-intro')}
                aria-label={t('welcome.buttons.importExistingWallet')}
              >
                {t('welcome.buttons.importExistingWallet')}
              </button>
              
              <button 
                type="button"
                className="welcome-text-btn" 
                onClick={() => setFlowStep('about-features')}
              >
                {t('welcome.buttons.learnMore')} <ArrowRightIcon />
              </button>
            </div>
          </div>
        )}
        
        {/* STEP: About Features (Part 1) */}
        {flowStep === 'about-features' && (
          <div className="welcome-step-content">
            <div className="welcome-intro-content">
              <div className="welcome-feature-list">
                <div className="welcome-feature-item">
                  <div className="welcome-feature-icon">
                    <ShieldIcon />
                  </div>
                  <h4>{t('welcome.features.control.title')}</h4>
                  <p>{t('welcome.features.control.description')}</p>
                </div>
                
                <div className="welcome-feature-item">
                  <div className="welcome-feature-icon">
                    <BriefcaseIcon />
                  </div>
                  <h4>{t('welcome.features.multiChain.title')}</h4>
                  <p>{t('welcome.features.multiChain.description')}</p>
                </div>
              </div>
            </div>
            
            <div className="welcome-actions">
              <button
                type="button"
                className="welcome-primary-btn"
                onClick={() => setFlowStep('about-features-more')}
                aria-label={t('welcome.buttons.moreFeatures')}
              >
                {t('welcome.buttons.moreFeatures')} <ArrowRightIcon />
              </button>
              
              <button 
                type="button"
                className="welcome-back-btn" 
                onClick={handleBack}
                aria-label={t('welcome.buttons.back')}
              >
                <BackIcon /> {t('welcome.buttons.back')}
              </button>
            </div>
          </div>
        )}
        
        {/* STEP: About Features (Part 2) */}
        {flowStep === 'about-features-more' && (
          <div className="welcome-step-content">
            <div className="welcome-intro-content">
              <div className="welcome-feature-list">
                <div className="welcome-feature-item">
                  <div className="welcome-feature-icon">
                    <KeyIcon />
                  </div>
                  <h4>{t('welcome.features.seedPhrase.title')}</h4>
                  <p>{t('welcome.features.seedPhrase.description')}</p>
                </div>
                
                <div className="welcome-feature-item">
                  <div className="welcome-feature-icon">
                    <InterfaceIcon />
                  </div>
                  <h4>{t('welcome.features.interface.title')}</h4>
                  <p>{t('welcome.features.interface.description')}</p>
                </div>
              </div>
            </div>
            
            <div className="welcome-actions">
              <button
                type="button"
                className="welcome-primary-btn"
                onClick={() => setFlowStep('create-intro')}
                aria-label={t('welcome.buttons.createNewWallet')}
              >
                {t('welcome.buttons.createNewWallet')}
              </button>

              <button
                type="button"
                className="welcome-secondary-btn"
                onClick={() => setFlowStep('import-intro')}
                aria-label={t('welcome.buttons.importExistingWallet')}
              >
                {t('welcome.buttons.importExistingWallet')}
              </button>
              
              <button 
                type="button"
                className="welcome-back-btn" 
                onClick={handleBack}
                aria-label={t('welcome.buttons.back')}
              >
                <BackIcon /> {t('welcome.buttons.back')}
              </button>
            </div>
          </div>
        )}
        
        {/* STEP: Create Intro */}
        {flowStep === 'create-intro' && (
          <div className="welcome-step-content">
            <div className="welcome-intro-content">
              <p className="welcome-info-text">
                {t('welcome.create.introText')}
              </p>
              
              <div className="welcome-steps-list">
                <div className="welcome-step-item">
                  <div className="welcome-step-number">1</div>
                  <div className="welcome-step-details">
                    <h3>{t('welcome.create.steps.generate.title')}</h3>
                    <p>{t('welcome.create.steps.generate.description')}</p>
                  </div>
                </div>
                
                <div className="welcome-step-item">
                  <div className="welcome-step-number">2</div>
                  <div className="welcome-step-details">
                    <h3>{t('welcome.create.steps.secure.title')}</h3>
                    <p>{t('welcome.create.steps.secure.description')}</p>
                  </div>
                </div>
                
                <div className="welcome-step-item">
                  <div className="welcome-step-number">3</div>
                  <div className="welcome-step-details">
                    <h3>{t('welcome.create.steps.verify.title')}</h3>
                    <p>{t('welcome.create.steps.verify.description')}</p>
                  </div>
                </div>
                
                <div className="welcome-step-item">
                  <div className="welcome-step-number">4</div>
                  <div className="welcome-step-details">
                    <h3>{t('welcome.create.steps.password.title')}</h3>
                    <p>{t('welcome.create.steps.password.description')}</p>
                  </div>
                </div>
              </div>
            </div>
            
            <div className="welcome-actions">
              <button
                type="button"
                className="welcome-primary-btn"
                onClick={() => setFlowStep('create-process')}
                aria-label={t('welcome.buttons.startWalletCreation')}
              >
                {t('welcome.buttons.startWalletCreation')}
              </button>
              
              <button 
                type="button"
                className="welcome-back-btn" 
                onClick={handleBack}
                aria-label={t('welcome.buttons.back')}
              >
                <BackIcon /> {t('welcome.buttons.back')}
              </button>
            </div>
          </div>
        )}
        
        {/* STEP: Create Process */}
        {flowStep === 'create-process' && (
          <div className="welcome-step-content">
            <div className="welcome-intro-content">
              <div className="welcome-security-note">
                <InfoIcon />
                <p>
                  {t('welcome.create.process.securityNote')}
                </p>
              </div>
              
              <div className="welcome-creation-details">
                <div className="welcome-detail-item">
                  <CheckIcon /> <span>{t('welcome.create.process.details.nonCustodial')}</span>
                </div>
                <div className="welcome-detail-item">
                  <CheckIcon /> <span>{t('welcome.create.process.details.localGeneration')}</span>
                </div>
                <div className="welcome-detail-item">
                  <CheckIcon /> <span>{t('welcome.create.process.details.compatibility')}</span>
                </div>
                <div className="welcome-detail-item">
                  <CheckIcon /> <span>{t('welcome.create.process.details.encryption')}</span>
                </div>
              </div>

              <div className="welcome-process-note">
                {t('welcome.create.process.buttonNote')}
              </div>
            </div>

            <div className="welcome-actions">
              <button
                type="button"
                className="welcome-primary-btn welcome-pulse-btn"
                onClick={handleCreateWallet}
                disabled={isLoading}
                aria-label={isLoading ? t('welcome.buttons.generatingWallet') : t('welcome.buttons.generateSecureWallet')}
              >
                {isLoading ? <><LoadingIcon /> {t('welcome.buttons.generatingWallet')}</> : t('welcome.buttons.generateSecureWallet')}
              </button>
              
              <button 
                type="button"
                className="welcome-back-btn" 
                onClick={handleBack}
                disabled={isLoading}
                aria-label={t('welcome.buttons.back')}
              >
                <BackIcon /> {t('welcome.buttons.back')}
              </button>
            </div>
          </div>
        )}
        
        {/* STEP: Create Confirm */}
        {flowStep === 'create-confirm' && (
          <div className="welcome-step-content">
            <div className="welcome-intro-content">
              <div className="welcome-security-explainer">
                <h3>{t('welcome.create.confirm.title')}</h3>
                <p>
                  {t('welcome.create.confirm.subtitle')}
                </p>
                
                <div className="welcome-security-points compact">
                  <div className="welcome-security-point">
                    <WarningIcon />
                    <p dangerouslySetInnerHTML={{ __html: t('welcome.create.confirm.points.onlyYou') }} />
                  </div>
                  
                  <div className="welcome-security-point">
                    <WarningIcon />
                    <p dangerouslySetInnerHTML={{ __html: t('welcome.create.confirm.points.loseSeedPhrase') }} />
                  </div>
                  
                  <div className="welcome-security-point">
                    <WarningIcon />
                    <p dangerouslySetInnerHTML={{ __html: t('welcome.create.confirm.points.noRecovery') }} />
                  </div>
                </div>
              </div>
            </div>
            
            <div className="welcome-actions">
              <button
                type="button"
                className="welcome-primary-btn"
                onClick={handleCreateWallet}
                disabled={isLoading}
                aria-label={isLoading ? t('welcome.buttons.creatingWallet') : t('welcome.buttons.understandCreate')}
              >
                {isLoading ? 
                  <><LoadingIcon /> {t('welcome.buttons.creatingWallet')}</> : 
                  t('welcome.buttons.understandCreate')}
              </button>
              
              <button 
                type="button"
                className="welcome-back-btn" 
                onClick={handleBack}
                disabled={isLoading}
                aria-label={t('welcome.buttons.back')}
              >
                <BackIcon /> {t('welcome.buttons.back')}
              </button>
            </div>
          </div>
        )}

        {/* STEP: Import Intro */}
        {flowStep === 'import-intro' && (
          <div className="welcome-step-content">
            <div className="welcome-intro-content">
              <p className="welcome-info-text">
                {t('welcome.import.introText')}
              </p>
              
              <div className="welcome-steps-list">
                <div className="welcome-step-item">
                  <div className="welcome-step-number">1</div>
                  <div className="welcome-step-details">
                    <h3>{t('welcome.import.steps.enterSeed.title')}</h3>
                    <p>{t('welcome.import.steps.enterSeed.description')}</p>
                  </div>
                </div>
                
                <div className="welcome-step-item">
                  <div className="welcome-step-number">2</div>
                  <div className="welcome-step-details">
                    <h3>{t('welcome.import.steps.verifyAddress.title')}</h3>
                    <p>{t('welcome.import.steps.verifyAddress.description')}</p>
                  </div>
                </div>
                
                <div className="welcome-step-item">
                  <div className="welcome-step-number">3</div>
                  <div className="welcome-step-details">
                    <h3>{t('welcome.import.steps.setupPassword.title')}</h3>
                    <p>{t('welcome.import.steps.setupPassword.description')}</p>
                  </div>
                </div>
              </div>
            </div>
            
            <div className="welcome-actions">
              <button
                type="button"
                className="welcome-primary-btn"
                onClick={() => setFlowStep('import-warning')}
                aria-label={t('welcome.buttons.continueToImport')}
              >
                {t('welcome.buttons.continueToImport')}
              </button>
              
              <button 
                type="button"
                className="welcome-back-btn" 
                onClick={handleBack}
                aria-label={t('welcome.buttons.back')}
              >
                <BackIcon /> {t('welcome.buttons.back')}
              </button>
            </div>
          </div>
        )}
        
        {/* STEP: Import Warning */}
        {flowStep === 'import-warning' && (
          <div className="welcome-step-content">
            <div className="welcome-intro-content">
              <div className="welcome-import-security-note">
                <WarningIcon />
                <div>
                  <h3>{t('welcome.import.warning.title')}</h3>
                  <p>{t('welcome.import.warning.subtitle')}</p>
                  <ul>
                    <li>{t('welcome.import.warning.points.trustedDevice')}</li>
                    <li>{t('welcome.import.warning.points.privateLocation')}</li>
                    <li>{t('welcome.import.warning.points.secureNetwork')}</li>
                  </ul>
                </div>
              </div>

              <div className="welcome-input-group">
                <label className="welcome-input-label">{t('welcome.import.seedPhraseLabel')}</label>
                <textarea
                  placeholder={t('welcome.import.seedPhrasePlaceholder')}
                  value={importInput}
                  onChange={(e) => setImportInput(e.target.value)}
                  className="welcome-import-textarea"
                  aria-label={t('welcome.import.seedPhraseAriaLabel')}
                  rows={4}
                />
                <p className="welcome-input-helper">
                  {t('welcome.import.seedPhraseHelper')}
                </p>
              </div>

              {importError && (
                <div className="welcome-error-message">
                  <WarningIcon />
                  <span>{importError}</span>
                </div>
              )}
            </div>

            <div className="welcome-actions">
              <button
                type="button"
                className="welcome-primary-btn"
                onClick={handleImportWallet}
                disabled={isLoading || !importInput}
                aria-label={isLoading ? t('welcome.buttons.importing') : t('welcome.buttons.importWallet')}
              >
                {isLoading ? <><LoadingIcon /> {t('welcome.buttons.importing')}</> : t('welcome.buttons.importWallet')}
              </button>
              
              <button 
                type="button"
                className="welcome-back-btn" 
                onClick={handleBack}
                disabled={isLoading}
                aria-label={t('welcome.buttons.back')}
              >
                <BackIcon /> {t('welcome.buttons.back')}
              </button>
            </div>
          </div>
        )}
        
        {/* STEP: Error Creating Wallet */}
        {flowStep === 'create-error' && (
          <div className="welcome-step-content">
            <div className="welcome-intro-content">
              <div className="welcome-error-container">
                <WarningIcon />
                <h3>{t('welcome.errors.createErrorTitle')}</h3>
                <p>{error || t('welcome.errors.unexpectedCreateError')}</p>
              </div>
            </div>
            
            <div className="welcome-actions">
              <button
                type="button"
                className="welcome-primary-btn"
                onClick={() => setFlowStep('create-process')}
                aria-label={t('welcome.buttons.tryAgain')}
              >
                {t('welcome.buttons.tryAgain')}
              </button>
              
              <button 
                type="button"
                className="welcome-back-btn" 
                onClick={handleBack}
                aria-label={t('welcome.buttons.back')}
              >
                <BackIcon /> {t('welcome.buttons.back')}
              </button>
            </div>
          </div>
        )}
        
        {/* STEP: Error Importing Wallet */}
        {flowStep === 'import-error' && (
          <div className="welcome-step-content">
            <div className="welcome-intro-content">
              <div className="welcome-error-container">
                <WarningIcon />
                <h3>{t('welcome.errors.importErrorTitle')}</h3>
                <p>{importError || t('welcome.errors.unexpectedImportError')}</p>
              </div>
              
              <div className="welcome-error-tips">
                <h4>{t('welcome.errors.commonIssuesTitle')}</h4>
                <ul>
                  <li>{t('welcome.errors.commonIssues.misspelled')}</li>
                  <li>{t('welcome.errors.commonIssues.extraSpaces')}</li>
                  <li>{t('welcome.errors.commonIssues.missingPrefix')}</li>
                </ul>
              </div>
            </div>
            
            <div className="welcome-actions">
              <button
                type="button"
                className="welcome-primary-btn"
                onClick={() => setFlowStep('import-warning')}
                aria-label={t('welcome.buttons.tryAgain')}
              >
                {t('welcome.buttons.tryAgain')}
              </button>
              
              <button 
                type="button"
                className="welcome-back-btn" 
                onClick={handleBack}
                aria-label={t('welcome.buttons.back')}
              >
                <BackIcon /> {t('welcome.buttons.back')}
              </button>
            </div>
          </div>
        )}
        
        {/* STEP: Wallet Created */}
        {flowStep === 'wallet-created' && (
          <div className="welcome-step-content">
            <div className="welcome-intro-content">
              <div className="welcome-success-icon">🎉</div>
              <p className="welcome-center-text">
                {t('welcome.walletCreated.successMessage')}
              </p>
              
              <div className="welcome-wallet-info-box">
                <div className="welcome-info-value">
                  <span className="welcome-info-label">{t('welcome.walletCreated.walletAddressLabel')}</span>
                  <span className="welcome-address-text">{formatAddress(tempWallet?.address)}</span>
                  <button
                    type="button"
                    className="welcome-copy-btn"
                    onClick={(e) => handleCopy(e, tempWallet?.address)}
                    aria-label={t('welcome.buttons.copyAddress')}
                  >
                    {showCopied ? t('welcome.buttons.copied') : t('welcome.buttons.copy')}
                  </button>
                </div>
              </div>
              
              <div className="welcome-next-instructions">
                <InfoIcon />
                <p dangerouslySetInnerHTML={{ __html: t('welcome.walletCreated.nextStepInstructions') }} />
              </div>
            </div>
            
            <div className="welcome-actions">
              <button 
                type="button"
                className="welcome-primary-btn welcome-pulse-btn" 
                onClick={() => setFlowStep('seed-phrase-info')} 
                aria-label={t('welcome.buttons.continueToSeedPhrase')}
              >
                {t('welcome.buttons.continueToSeedPhrase')} <ArrowRightIcon />
              </button>
            </div>
          </div>
        )}
        
        {/* STEP: Address Info (for imported wallets) */}
        {flowStep === 'address-info' && (
          <div className="welcome-step-content">
            <div className="welcome-intro-content">
              <div className="welcome-success-icon">✅</div>
              <p className="welcome-center-text">
                {t('welcome.addressInfo.successMessage')}
              </p>
              
              <div className="welcome-wallet-info-box">
                <div className="welcome-info-value">
                  <span className="welcome-info-label">{t('welcome.addressInfo.walletAddressLabel')}</span>
                  <span className="welcome-address-text">{formatAddress(tempWallet?.address)}</span>
                  <button
                    type="button"
                    className="welcome-copy-btn"
                    onClick={(e) => handleCopy(e, tempWallet?.address)}
                    aria-label={t('welcome.buttons.copyAddress')}
                  >
                    {showCopied ? t('welcome.buttons.copied') : t('welcome.buttons.copy')}
                  </button>
                </div>
              </div>
              
              <div className="welcome-next-instructions">
                <InfoIcon />
                <p>{t('welcome.addressInfo.nextStepInstructions')}</p>
              </div>
            </div>
            
            <div className="welcome-actions">
              <button 
                type="button"
                className="welcome-primary-btn" 
                onClick={() => setFlowStep('security-confirmation')} 
                aria-label={t('welcome.buttons.continueToSecuritySetup')}
              >
                {t('welcome.buttons.continueToSecuritySetup')} <ArrowRightIcon />
              </button>
              
              <button 
                type="button"
                className="welcome-back-btn" 
                onClick={handleBack}
                aria-label={t('welcome.buttons.back')}
              >
                <BackIcon /> {t('welcome.buttons.back')}
              </button>
            </div>
          </div>
        )}
        
        {/* STEP: Seed Phrase Info */}
        {flowStep === 'seed-phrase-info' && (
          <div className="welcome-step-content">
            <div className="welcome-intro-content">
              <div className="welcome-key-security-box">
                <WarningIcon />
                <h3 className="welcome-key-security-title">{t('welcome.seedPhrase.criticalSecurityStep')}</h3>
                <p className="welcome-key-security-desc" dangerouslySetInnerHTML={{ __html: t('welcome.seedPhrase.criticalDescription') }} />
              </div>

              <div className="welcome-wallet-info-box welcome-seed-phrase-box">
                <div className="welcome-info-value">
                  <span className="welcome-info-label">{t('welcome.seedPhrase.wordLabel')}</span>
                  <div className="welcome-seed-phrase-container">
                    {showFullSeed ? (
                      <div className="welcome-seed-phrase-grid">
                        {/* UPDATED: Use getSeedPhraseForUI helper */}
                        {(() => {
                          const seedPhrase = getSeedPhraseForUI();
                          if (seedPhrase) {
                            return seedPhrase.split(' ').map((word, index) => (
                              <div key={index} className="welcome-seed-word">
                                <span className="welcome-seed-index">{index + 1}.</span> {word}
                              </div>
                            ));
                          }
                          return <div className="welcome-error-text">Error retrieving seed phrase</div>;
                        })()}
                      </div>
                    ) : (
                      <div className="welcome-blurred-seed">
                        <span className="welcome-blurred-text">● ● ● ● ● ● ● ● ● ● ● ●</span>
                      </div>
                    )}
                    <div className="welcome-key-actions">
                      <button
                        type="button"
                        className="welcome-visibility-btn"
                        onClick={toggleSeedVisibility}
                        aria-label={showFullSeed ? t('welcome.buttons.hideSeedPhrase') : t('welcome.buttons.showSeedPhrase')}
                      >
                        {showFullSeed ? t('welcome.buttons.hide') : t('welcome.buttons.show')}
                      </button>
                      {showFullSeed && (
                        <button
                          type="button"
                          className="welcome-copy-btn"
                          onClick={(e) => {
                            // UPDATED: Use getSeedPhraseForUI helper for copy
                            const seedPhrase = getSeedPhraseForUI();
                            if (seedPhrase) {
                              handleCopy(e, seedPhrase);
                            }
                          }}
                          aria-label={t('welcome.buttons.copySeedPhrase')}
                        >
                          {showCopied ? t('welcome.buttons.copied') : t('welcome.buttons.copy')}
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              {/* SECURITY IMPROVEMENT: Add auto-hide warning */}
              {showFullSeed && (
                <div className="welcome-auto-hide-warning">
                  <WarningIcon />
                  <p>{t('settings.security.recovery.autoHideWarning', { defaultValue: 'For your security, seed phrase display will automatically hide after 2 minutes.' })}</p>
                </div>
              )}

              <div className="welcome-instructions">
                <h3>{t('welcome.seedPhrase.saveInstructions')}</h3>
                <p className="welcome-instruction-text">
                  ✅ {t('welcome.seedPhrase.storageInstructions.writeDown')}
                </p>
                <p className="welcome-instruction-text">
                  ✅ {t('welcome.seedPhrase.storageInstructions.passwordManager')}
                </p>
                <p className="welcome-instruction-text">
                  ❌ {t('welcome.seedPhrase.storageInstructions.neverShare')}
                </p>
                <p className="welcome-instruction-text">
                  ❌ {t('welcome.seedPhrase.storageInstructions.neverPlainText')}
                </p>
              </div>
            </div>
            
            <div className="welcome-actions">
              <button 
                type="button"
                className="welcome-primary-btn" 
                onClick={() => setFlowStep('verify-seed')} 
                aria-label={t('welcome.buttons.savedSeedPhrase')}
              >
                {t('welcome.buttons.savedSeedPhrase')} <CheckIcon />
              </button>
              
              <button 
                type="button"
                className="welcome-back-btn" 
                onClick={handleBack}
                aria-label={t('welcome.buttons.back')}
              >
                <BackIcon /> {t('welcome.buttons.back')}
              </button>
            </div>
          </div>
        )}
        
        {/* STEP: Verify Seed - Click words in order */}
        {flowStep === 'verify-seed' && (
          <div className="welcome-step-content">
            <div className="welcome-intro-content">
              <p className="welcome-info-text">
                {t('welcome.verify.instructions')}
              </p>
              
{/* Display selected words in order */}
<div className="welcome-selected-words-container">
  <div className="welcome-selected-words">
    {selectedSeedWords.length > 0 ? (
      selectedSeedWords.map((word, index) => (
        <div 
          key={index} 
          className="welcome-selected-word welcome-selectable" 
          onClick={() => handleDeselectSeedWord(word, index)}
          title={t('welcome.verify.clickToRemove')}
        >
          <span className="welcome-word-number">{index + 1}.</span> {word}
        </div>
      ))
    ) : (
      <div className="welcome-no-words-selected">
        {t('welcome.verify.selectWordsInOrder')}
      </div>
    )}
  </div>
</div>
              
              {/* Display shuffled words for selection */}
              <div className="welcome-seed-selection">
                {shuffledSeedWords.map((word, index) => (
                  <div 
                    key={index} 
                    className="welcome-selectable-word"
                    onClick={() => handleSelectSeedWord(word)}
                  >
                    {word}
                  </div>
                ))}
              </div>

              {seedError && (
                <div className="welcome-error-message">
                  <WarningIcon />
                  <span>{seedError}</span>
                </div>
              )}
              
              {isSeedVerified && (
                <div className="welcome-success-message">
                  <CheckIcon />
                  <span>{t('welcome.seedPhrase.verified')}</span>
                </div>
              )}
              
              <button
                type="button"
                className="welcome-text-btn"
                onClick={() => setFlowStep('verify-seed-manual')}
              >
                {t('welcome.verify.manualEntry')}
              </button>
            </div>
            
            <div className="welcome-actions">
              <button 
                type="button"
                className="welcome-back-btn" 
                onClick={handleBack}
                aria-label={t('welcome.buttons.back')}
              >
                <BackIcon /> {t('welcome.buttons.back')}
              </button>
            </div>
          </div>
        )}
        
        {/* STEP: Verify Seed - Manual entry (alternative method) */}
        {flowStep === 'verify-seed-manual' && (
          <div className="welcome-step-content">
            <div className="welcome-intro-content">
              <p className="welcome-info-text">
                {t('welcome.verify.manualInstructions')}
              </p>

              <div className="welcome-input-group">
                <label className="welcome-input-label">{t('welcome.verify.enterSeedPhrase')}</label>
                <textarea
                  ref={seedInputRef}
                  placeholder={t('welcome.verify.enterSeedPhrasePlaceholder')}
                  value={confirmSeedPhrase}
                  onChange={(e) => setConfirmSeedPhrase(e.target.value)}
                  className="welcome-import-textarea"
                  aria-label={t('welcome.verify.confirmSeedPhraseAriaLabel')}
                  rows={4}
                />
                <p className="welcome-input-helper">
                  {t('welcome.verify.seedPhraseHelperText')}
                </p>
              </div>

              {seedError && (
                <div className="welcome-error-message">
                  <WarningIcon />
                  <span>{seedError}</span>
                </div>
              )}
              
              <button
                type="button"
                className="welcome-text-btn"
                onClick={toggleSeedVisibility}
              >
                {showFullSeed ? t('welcome.verify.hideSeedPhrase') : t('welcome.verify.showSeedPhrase')}
              </button>
              
              {showFullSeed && (
                <div className="welcome-key-reminder">
                  {/* UPDATED: Use getSeedPhraseForUI helper */}
                  <p>
                    {getSeedPhraseForUI() || t('welcome.errors.seedPhraseNotAvailable')}
                  </p>
                </div>
              )}
            </div>
            
            <div className="welcome-actions">
              <button
                type="button"
                className="welcome-primary-btn"
                onClick={verifySeedPhraseSaved}
                aria-label={t('welcome.verify.verifyButtonLabel')}
              >
                {t('welcome.verify.verifyButtonText')}
              </button>
              
              <button 
                type="button"
                className="welcome-back-btn" 
                onClick={() => setFlowStep('verify-seed')}
                aria-label={t('welcome.buttons.backToWordSelection')}
              >
                <BackIcon /> {t('welcome.buttons.backToWordSelection')}
              </button>
            </div>
          </div>
        )}
        
        {/* STEP: Security Confirmation */}
        {flowStep === 'security-confirmation' && (
          <div className="welcome-step-content">
            <div className="welcome-intro-content">
              <p className="welcome-info-text">
                {t('welcome.security.instructions')}
              </p>

              <div className="welcome-security-checklist">
                <div className="welcome-checklist-item">
                  <div 
                    className="welcome-checkbox-container" 
                    role="checkbox" 
                    aria-checked={userConfirmed} 
                    tabIndex="0" 
                    onClick={() => setUserConfirmed(!userConfirmed)}
                  >
                    <input
                      type="checkbox"
                      className="welcome-custom-checkbox"
                      id="confirm-checkbox"
                      checked={userConfirmed}
                      onChange={(e) => setUserConfirmed(e.target.checked)}
                    />
                    <label className="welcome-checkbox-label" htmlFor="confirm-checkbox">
                      {t('welcome.security.checkboxes.understand')}
                    </label>
                  </div>
                </div>
                
                <div className="welcome-checklist-item">
                  <div 
                    className="welcome-checkbox-container" 
                    role="checkbox" 
                    aria-checked={securityAcknowledged} 
                    tabIndex="0" 
                    onClick={() => setSecurityAcknowledged(!securityAcknowledged)}
                  >
                    <input
                      type="checkbox"
                      className="welcome-custom-checkbox"
                      id="security-checkbox"
                      checked={securityAcknowledged}
                      onChange={(e) => setSecurityAcknowledged(e.target.checked)}
                    />
                    <label className="welcome-checkbox-label" htmlFor="security-checkbox">
                      {t('welcome.security.checkboxes.nonCustodial')}
                    </label>
                  </div>
                </div>
              </div>
            </div>
            
            <div className="welcome-actions">
              <button
                type="button"
                className="welcome-primary-btn"
                onClick={() => setFlowStep('password-setup')}
                disabled={!userConfirmed || !securityAcknowledged}
                aria-label={t('welcome.buttons.continueToSecuritySetup')}
              >
                {t('welcome.buttons.continueToSecuritySetup')}
              </button>
              
              <button 
                type="button"
                className="welcome-back-btn" 
                onClick={handleBack}
                aria-label={t('welcome.buttons.back')}
              >
                <BackIcon /> {t('welcome.buttons.back')}
              </button>
            </div>
          </div>
        )}
        
        {/* STEP: Password Setup */}
        {flowStep === 'password-setup' && (
          <div className="welcome-step-content">
            <div className="welcome-intro-content">
              <div className="welcome-security-explainer">
                <h3 className="welcome-explainer-title">{t('welcome.passwords.title')}</h3>
                <p className="welcome-explainer-text">
                  {t('welcome.passwords.explainer')}
                </p>
                <ol className="welcome-security-layers">
                  <li className="welcome-security-layer">
                    {t('welcome.passwords.layers.basicEncryption')}
                  </li>
                  <li className="welcome-security-layer">
                    {t('welcome.passwords.layers.optionalPassword')}
                  </li>
                </ol>
              </div>

              <div className="welcome-password-setup-container">
                <div className="welcome-input-group">
                  <label className="welcome-input-label">{t('welcome.passwords.createPassword')}</label>
                  <input
                    type="password"
                    placeholder={t('welcome.passwords.passwordPlaceholder')}
                    value={walletPassword}
                    onChange={(e) => setWalletPasswordInput(e.target.value)}
                    className="welcome-import-input"
                    autoComplete="new-password"
                    minLength="8"
                    disabled={skipPassword}
                  />
                  
                  {/* Password strength indicator */}
                  {passwordFeedback && !skipPassword && (
                    <div className="welcome-password-strength-meter">
                      <div 
                        className="welcome-strength-bar" 
                        style={{ 
                          width: `${(passwordStrength + 1) * 20}%`,
                          backgroundColor: passwordFeedback.color 
                        }}
                      />
                      <div className="welcome-strength-text">{passwordFeedback.feedback}</div>
                    </div>
                  )}
                </div>
                
                <div className="welcome-input-group">
                  <label className="welcome-input-label">{t('welcome.passwords.confirmPassword')}</label>
                  <input
                    type="password"
                    placeholder={t('welcome.passwords.confirmPasswordPlaceholder')}
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    className="welcome-import-input"
                    autoComplete="new-password"
                    disabled={skipPassword}
                  />
                </div>
                
                <div className="welcome-skip-password-option">
                  <div 
                    className="welcome-checkbox-container" 
                    onClick={() => setSkipPassword(!skipPassword)}
                  >
                    <input
                      type="checkbox"
                      className="welcome-custom-checkbox"
                      id="skip-password"
                      checked={skipPassword}
                      onChange={(e) => setSkipPassword(e.target.checked)}
                    />
                    <label className="welcome-checkbox-label" htmlFor="skip-password">
                      {t('welcome.passwords.skipOption')}
                    </label>
                  </div>
                  
                  {skipPassword && (
                    <div className="welcome-skip-warning">
                      <p>
                        {t('welcome.passwords.skipWarning')}
                      </p>
                    </div>
                  )}
                </div>
              </div>
              
              {!skipPassword && walletPassword && passwordStrength < 3 && (
                <p className="welcome-password-warning">
                  <WarningIcon /> {t('welcome.passwords.tooWeak')}
                </p>
              )}

              {!skipPassword && walletPassword && confirmPassword && walletPassword !== confirmPassword && (
                <p className="welcome-password-warning">
                  <WarningIcon /> {t('welcome.passwords.dontMatch')}
                </p>
              )}

              {error && (
                <div className="welcome-error-message">
                  <WarningIcon />
                  <span>{error}</span>
                </div>
              )}
            </div>
            
            <div className="welcome-actions">
              <button
                type="button"
                className="welcome-primary-btn"
                onClick={handleSetupSecurity}
                disabled={
                  isLoading || 
                  (!skipPassword && (
                    !walletPassword || 
                    !confirmPassword || 
                    walletPassword !== confirmPassword || 
                    passwordStrength < 3
                  ))
                }
                aria-label={isLoading ? t('welcome.buttons.settingUp') : t('welcome.buttons.completeSetup')}
              >
                {isLoading ? (
                  <><LoadingIcon /> {t('buttons.settingUp')}</>
                ) : (
                  t('welcome.buttons.completeSetup')
                )}
              </button>
              
              <button 
                type="button"
                className="welcome-back-btn" 
                onClick={handleBack}
                disabled={isLoading}
                aria-label={t('welcome.buttons.back')}
              >
                <BackIcon /> {t('welcome.buttons.back')}
              </button>
            </div>
          </div>
        )}
        
        {/* STEP: Setup Success */}
        {flowStep === 'setup-success' && (
          <div className="welcome-step-content">
            <div className="welcome-intro-content welcome-success-content">
              <div className="welcome-success-icon">🎉</div>
              <h2>{t('welcome.success.title')}</h2>
              <p>{t('welcome.success.description')}</p>
              <div className="welcome-loading-indicator welcome-large">
                <div className="welcome-loading-circle"></div>
                <div className="welcome-loading-circle"></div>
                <div className="welcome-loading-circle"></div>
              </div>
              <p className="welcome-small-text">{t('welcome.success.loading')}</p>
            </div>
          </div>
        )}
      </div>
      {/* Add PWA Installation Banner - Always show on welcome page */}
      <InstallPWABanner alwaysShow={true} />
      {/* Secure footer */}
      <div className="welcome-secure-footer">
        <ShieldIcon /> {t('footer.securityMessage')}
      </div>
    </div>
  );
};

export default WelcomePage;